/**
 * Internal tRPC setup for Astro standalone app.
 * Uses Firebase auth for authentication.
 *
 * IMPORTANT: This module should NOT be imported by components that are exposed
 * via Module Federation. Those components should use useAstroTRPC from trpcContext.tsx
 * to get the tRPC client from context (provided by either Astro or the host app).
 *
 * @see ./trpcContext.tsx - Context-based hook for federated components
 * @see ../providers/trpc.tsx - Provider that connects this to the context
 */
import { QueryClient } from '@tanstack/react-query';
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
  wsLink,
  createWSClient,
} from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@astro/api/router';
import { auth } from './firebase';
import { createAuthRetry } from './authRetry';
import { isUnbatchedProcedure } from './unbatchedProcedures';

const forceRefreshToken = async (): Promise<void> => {
  await auth.currentUser?.getIdToken(true);
};

const {
  link: authRetryLink,
  withPendingRefresh,
  triggerRefresh,
} = createAuthRetry(forceRefreshToken);

export const queryClient = new QueryClient();

// Only fires if Firebase never settles at all (a blocked IndexedDB open), so no
// healthy session waits on it. Without it one wedged read stalls every request.
const AUTH_INIT_DEADLINE_MS = 10_000;

let authSettled = false;
let authDeadline: ReturnType<typeof setTimeout> | undefined;

// Firebase restores a persisted session asynchronously; authStateReady resolves
// once that settles, with a user or with nobody. Settled once per page load
// rather than per request, since that is how often it happens.
const authReady: Promise<void> = Promise.race([
  auth.authStateReady().then(() => {
    authSettled = true;
  }),
  new Promise<void>((resolve) => {
    authDeadline = setTimeout(() => {
      // Promise.race leaves the loser running, so a healthy settle must not log.
      if (!authSettled) {
        console.error('[Astro] Firebase auth did not settle; continuing without a token');
      }
      resolve();
    }, AUTH_INIT_DEADLINE_MS);
  }),
])
  .catch((error) => {
    // Requests continue without a token rather than failing the whole batch.
    console.error('[Astro] Firebase auth state failed to settle', error);
  })
  .finally(() => clearTimeout(authDeadline));

const getAuthToken = async (): Promise<string | undefined> => {
  await authReady;
  try {
    return await auth.currentUser?.getIdToken();
  } catch {
    return undefined;
  }
};

// TanStack Query Devtools ------------------
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

// This code is for all users
window.__TANSTACK_QUERY_CLIENT__ = queryClient;
console.log('[Astro] QueryClient exposed to window for devtools');
// End of Devtools setup ------------------

// Get base URL for WebSocket (convert http/https to ws/wss)
const getBaseUrl = () => {
  const httpUrl = import.meta.env.VITE_TRPC_URL;
  return httpUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
};

// Create WebSocket client
// Note: The server extracts auth from query params in the WebSocket URL
// For initial setup, we'll create the client with timezone only
// Auth token will be included when the WebSocket connects (handled by createWSClient)
// In production, you may want to recreate the client when auth changes
const baseSubscriptionUrl = getBaseUrl();
let timezone = 'America/Chicago'; // fallback
try {
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
} catch {
  // fallback
}

// Create WebSocket client - auth will be handled via query params
// The server checks req.headers.authorization first, then falls back to query params
// Since browsers can't set custom headers on WebSocket connections, we pass auth via URL
const wsClient = createWSClient({
  url: async () => {
    const token = await withPendingRefresh(getAuthToken);

    if (!token) {
      console.warn('WebSocket connecting without auth token - subscription may fail');
    }

    const authParam = token ? `&authorization=${encodeURIComponent(token)}` : '';
    return `${baseSubscriptionUrl}?timezone=${encodeURIComponent(timezone)}${authParam}`;
  },
  // Exponential backoff for reconnection attempts
  // Formula: min(1000 * 2^attemptIndex, 30000) - caps at 30 seconds
  retryDelayMs: (attemptIndex) => {
    const delay = Math.min(1000 * Math.pow(2, attemptIndex), 30000);
    return delay;
  },
  // Keep-alive ping/pong to detect dead connections
  keepAlive: {
    enabled: true,
    intervalMs: 30000, // Send ping every 30 seconds
    pongTimeoutMs: 5000, // Wait 5 seconds for pong response before considering connection dead
  },
  onOpen: () => {},
  onClose: (cause) => {
    if (cause) {
      console.warn('WebSocket connection closed:', {
        ...cause,
      });
    }
    // A 1000 close is a normal closure, not an auth failure, so only the others
    // earn a fresh token for the reconnect. Routed through triggerRefresh so the
    // reconnecting url() call's withPendingRefresh() waits for it.
    if (cause?.code !== 1000) {
      triggerRefresh();
    }
  },
  onError: (event) => {
    console.error('WebSocket connection error:', event);
  },
});

const httpLinkOptions = {
  url: import.meta.env.VITE_TRPC_URL,
  headers: async () => {
    let timezone = 'America/Chicago'; // fallback
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // fallback to UTC or use a library like moment-timezone
    }
    const token = await withPendingRefresh(getAuthToken);
    // Omit authorization entirely when unauthenticated — sending
    // "Bearer undefined" makes protected procedures 401.
    return token ? { authorization: `Bearer ${token}`, timezone } : { timezone };
  },
};

const trpcClient = createTRPCClient<AppRouter>({
  links: [
    authRetryLink,
    splitLink({
      condition: (op) => op.type === 'subscription',
      true: wsLink<AppRouter>({
        client: wsClient,
      }),
      false: splitLink({
        condition: (op) => isUnbatchedProcedure(op.path),
        true: httpLink(httpLinkOptions),
        false: httpBatchLink(httpLinkOptions),
      }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

// Export the client directly for subscriptions
export { trpcClient };
